// Roster hooks — the WRAPPER layer (lint-exempt for raw TanStack Query). The roster
// rail + the org-lens navigation live in the ORG entity realm: keyed under ['org', orgId, …] so they survive a
// band switch (which scorches ['band', …]). The rail is reach-scoped + born-blank server-side — an
// unassigned staffer gets an empty list, NEVER placeholder artists.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useActiveOrg } from "../activeOrg.js";
import { ApiError } from "./apiError.js";
import { useMe } from "./useSession.js";

export type RosterArtistView = components["schemas"]["RosterArtistView"];
export type RosterArtistsRead = components["schemas"]["RosterArtistsRead"];
export type RosterOverviewRead = components["schemas"]["RosterOverviewRead"];
export type RosterAttentionRead = components["schemas"]["RosterAttentionRead"];
export type RosterEventsFeedRead = components["schemas"]["RosterEventsFeedRead"];
export type NeedsAttentionRead = components["schemas"]["NeedsAttentionRead"];
export type RosterTodosTodayRead = components["schemas"]["RosterTodosTodayRead"];
export type RosterTodoRow = components["schemas"]["RosterTodoRow"];
type SeatSummary = components["schemas"]["SeatSummary"];

type LinkCodeIssued = components["schemas"]["LinkCodeIssued"];
type LinkRedeemed = components["schemas"]["LinkRedeemed"];
export type { LinkCodeIssued, LinkRedeemed };

/**
 * `POST /orgs/{orgId}/teams/link-code` — the org side of "Link a team" (PO, 2026-08-20): mint the
 * one-time XXXX-XXXX code an artist redeems from their Contacts page. Errors are the modal's, inline.
 */
export function useOrgGenerateLinkCode(orgId: string | undefined) {
  const { api } = useApp();
  return useMutation<LinkCodeIssued, ApiError, void>({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/api/v1/orgs/{orgId}/teams/link-code", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}

/**
 * `POST /orgs/{orgId}/teams/link-code/redeem` ({code}) — redeem a code an ARTIST generated. Success
 * invalidates the roster-artists rail; NOTE the new band appears there only once the org is also
 * ASSIGNED — the link itself lands in the artist's Your Teams instantly.
 */
export function useOrgRedeemLinkCode(orgId: string | undefined) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation<LinkRedeemed, ApiError, string>({
    mutationFn: async (code) => {
      const { data, error, response } = await api.POST("/api/v1/orgs/{orgId}/teams/link-code/redeem", {
        params: { path: { orgId: orgId as string } },
        body: { code },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", orgId ?? "none", "roster", "artists"] });
    },
  });
}

/** The cross-roster To-Dos widget filters: per-artist, assigned-to-me variant, Archive toggle.*/
export interface RosterTodoFilters {
  bandId?: string;
  assignedToMe?: boolean;
  includeArchived?: boolean;
}

/** The resolved active-org seat: which org the roster shell is scoped to, plus its subview + all org seats. */
export interface ResolvedOrg {
  orgId: string | undefined;
  /** The org seat's `root` — label/management/booking — i.e. the roster SUBVIEW (never client-derived).*/
  subview: string | undefined;
  /** The active org seat (or undefined while /me loads / no org seat). */
  seat: SeatSummary | undefined;
  /** Every org seat the person holds — >1 means a manual subview switcher renders (the jedi-email gate dies). */
  orgSeats: SeatSummary[];
}

/**
 * Resolve the active org from /me + the {@link useActiveOrg} selection. The subview is the seat's
 * `root`, computed server-side — the RosterShell renders it, it never derives one. Defaults to the first org seat;
 * an explicit selection (multi-seat switch) wins iff it still matches a held seat.
 */
export function useResolvedOrg(): ResolvedOrg {
  const me = useMe();
  const selected = useActiveOrg((s) => s.activeOrgId);
  const orgSeats = (me.data?.seats ?? []).filter((s) => s.kind === "org");
  const seat = orgSeats.find((s) => s.orgId === selected) ?? orgSeats[0];
  return { orgId: seat?.orgId ?? undefined, subview: seat?.root ?? undefined, seat, orgSeats };
}

/** `GET /orgs/{orgId}/roster/artists` — the reach-scoped assigned-artist rail.*/
export function useRosterArtists(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "roster", "artists"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/artists", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.items ?? [];
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /orgs/{orgId}/roster/overview` — the composed Label cross-roster overview: header + KPI strip
 * + Roster Pulse + Trending + Tasks. MONEY-FREE by construction — the DTO has no money field. Org-scoped
 * (keyed ['org', …] so it survives an artist switch), reach-scoped + born-blank server-side (an unassigned staffer
 * aggregates to zero). Enveloped read → returns `data.data`.
 */
export function useRosterOverview(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "roster", "overview"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/overview", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /orgs/{orgId}/roster/attention` — the Management triage read: overdue tasks, expiring
 * holds, active rollouts, new opportunities. MONEY-FREE by construction (the DTO has no money field). Org-scoped
 * (keyed ['org', …] so it survives an artist switch), reach-scoped + born-blank server-side (an unassigned
 * staffer aggregates to zero). Enveloped read → returns `data.data`. Mirrors {@link useRosterOverview} exactly.
 */
export function useRosterAttention(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "roster", "attention"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/attention", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /orgs/{orgId}/roster/needs-attention` — Needs attention, ALL ROSTER (ART-324): one row per reached artist,
 * their single most pressing item for this lens. MONEY-FREE. Org-scoped (keyed ['org', …] so it survives an artist
 * switch); enveloped → returns `data.data`.
 */
export function useRosterNeedsAttention(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "roster", "needs-attention"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/needs-attention", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /orgs/{orgId}/roster/events-feed` — the Management upcoming-events strip: the next-N-days
 * shows / releases / content / deadlines across the reachable roster. MONEY-FREE by construction. Org-scoped
 * (keyed ['org', …]), born-blank server-side. Enveloped read → returns `data.data`. Mirrors useRosterOverview.
 */
export function useRosterEventsFeed(orgId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["org", orgId ?? "none", "roster", "events-feed"],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/events-feed", {
        params: { path: { orgId: orgId as string } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /orgs/{orgId}/roster/todos-today` — the cross-roster To-Dos aggregate: every task across the
 * caller's reach set the caller's per-band team may see (a hidden-from-label task never appears in a label context —
 * RLS-enforced server-side). MONEY-FREE by construction. Org-scoped (keyed `['org', orgId, 'roster', 'todos', …]`
 * so it survives an artist switch AND `roster-todos-changed` can invalidate every filter variant by prefix),
 * reach-scoped + born-blank server-side (an unassigned staffer gets zero rows). The server resolves the viewer's
 * team per band — NO client subview/team hint is sent (only the bandId / assignedToMe / includeArchived filters,
 * which narrow, never widen). Enveloped read → returns `data.data`.
 */
export function useRosterTodos(orgId: string | undefined, filters: RosterTodoFilters = {}) {
  const { api } = useApp();
  const { bandId, assignedToMe = false, includeArchived = false } = filters;
  return useQuery({
    queryKey: ["org", orgId ?? "none", "roster", "todos", { bandId: bandId ?? null, assignedToMe, includeArchived }],
    enabled: orgId != null && orgId !== "",
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/todos-today", {
        params: {
          path: { orgId: orgId as string },
          query: { bandId, assignedToMe, includeArchived },
        },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    staleTime: 30_000,
  });
}

/** The `/me/navigation?context=` value for an org-home document (subview tab-set + org surfaces). */
export function orgHomeContext(orgId: string): string {
  return `org:${orgId}`;
}

/**
 * The `/me/navigation?context=` value for the ORG-LENS of an artist — resolves the effective role +
 * money-free-ness under the org, server-side. Mirrors the `/orgs/{orgId}/bands/{bandId}` API route.
 */
export function orgLensContext(orgId: string, bandId: string): string {
  return `org:${orgId}:band:${bandId}`;
}

/** The subview label for the chip (label/management/booking → Label/Management/Booking). Display-only. */
export function subviewLabel(subview: string | null | undefined): string {
  switch (subview) {
    case "label":
      return "Label";
    case "management":
      return "Management";
    case "booking":
      return "Booking";
    default:
      return "Roster";
  }
}
